import { useState } from "react";
import SuccessPopup from "@/components/feature/SuccessPopup";

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [errorSubmit, setErrorSubmit] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formErrors, setFormErrors] = useState({ name: '', email: '', message: '' });

  const validate = () => {
    const errors = { name: '', email: '', message: '' };
    if (formData.name.trim().length < 3) errors.name = 'Name must be at least 3 characters';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Enter a valid email address';
    }
    if (formData.message.trim().length < 20) errors.message = 'Message must be at least 20 characters';
    setFormErrors(errors);
    return !errors.name && !errors.email && !errors.message;
  };

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setErrorSubmit(false);

    let text = '';
    for (const [key, value] of Object.entries(formData)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: formData.email,
      text,
      problemType: 'General Inquiry',
      to_email: 'support@app-for-security.com',
      subject: 'Contact Request',
    };

    try {
      const res = await fetch('https://rocknlabs.com/api/support/request_form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Server error');

      setIsSuccessOpen(true);
      setFormData({ name: '', email: '', message: '' });
      setFormErrors({ name: '', email: '', message: '' });
    } catch {
      setErrorSubmit(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section className="relative h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden bg-foreground-900">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900 to-foreground-900" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight font-heading">
            Contact Us
          </h1>
          <p className="mt-4 text-lg text-foreground-300 max-w-2xl mx-auto">
            Get in touch with our team for inquiries and support
          </p>
        </div>
      </section>

      <section className="relative py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-xl mx-auto">
            <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200">
              <form
                id="contact-form-page"
                onSubmit={handleSubmit}
              >
                <div className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">Name</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => { setFormData((prev) => ({ ...prev, name: e.target.value })); setFormErrors((prev) => ({ ...prev, name: '' })); }}
                      className={`w-full px-4 py-3 rounded-lg border bg-white text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent ${formErrors.name ? 'border-red-400' : 'border-background-200'}`}
                      placeholder="Your name"
                    />
                    {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">Email</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => { setFormData((prev) => ({ ...prev, email: e.target.value })); setFormErrors((prev) => ({ ...prev, email: '' })); }}
                      className={`w-full px-4 py-3 rounded-lg border bg-white text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent ${formErrors.email ? 'border-red-400' : 'border-background-200'}`}
                      placeholder="your@email.com"
                    />
                    {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">Message</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={(e) => { setFormData((prev) => ({ ...prev, message: e.target.value })); setFormErrors((prev) => ({ ...prev, message: '' })); }}
                      className={`w-full px-4 py-3 rounded-lg border bg-white text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none ${formErrors.message ? 'border-red-400' : 'border-background-200'}`}
                      placeholder="How can we help you?"
                    />
                    <p className="text-xs text-foreground-500 mt-1">Maximum 500 characters</p>
                    {formErrors.message && <p className="text-xs text-red-500 mt-1">{formErrors.message}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary-500 text-white font-medium py-3 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                  {errorSubmit && (
                    <p className="text-sm text-red-600 text-center">Something went wrong. Please try again.</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
          {isSuccessOpen && (
             <SuccessPopup onClose={() => setIsSuccessOpen(false)} />
         )}
      </section>
    </main>
  );
}